import { f0FormField, useF0Form, useF0FormDefinition } from '@factorialco/f0-react'
import { FactorialF0Form } from 'modules/core/components/FactorialF0Form'
import { useEffect } from 'react'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'
import useSubmissionPeriods from 'modules/planning/hooks/useSubmissionPeriods'

interface ExistingSubmissionPeriod {
  id: string
  startDate: string
  endDate: string
}

interface Props {
  periodId: string
  companyId: string
  submissionPeriod?: ExistingSubmissionPeriod
  onClose: () => void
  onStatusChange: (status: {
    submit: () => void
    isSubmitting: boolean
    hasErrors: boolean
  }) => void
}

const formatDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const ScheduleForLater = ({
  periodId,
  companyId,
  submissionPeriod,
  onClose,
  onStatusChange,
}: Props) => {
  const isEditing = !!submissionPeriod
  const { bulkCreate, update } = useSubmissionPeriods({
    periodId,
    companyId,
    submissionPeriodId: submissionPeriod?.id,
  })

  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  useEffect(() => {
    onStatusChange({ submit, isSubmitting, hasErrors })
  }, [submit, isSubmitting, hasErrors, onStatusChange])

  const schema = z.object({
    startDate: f0FormField(z.date(), {
      label: i18n.t('planning.submission_period.form.start_date'),
      fieldType: 'date',
      row: 'dates',
    }),
    endDate: f0FormField(z.date(), {
      label: i18n.t('planning.submission_period.form.end_date'),
      fieldType: 'date',
      row: 'dates',
    }),
  })

  const defaultValues = (() => {
    if (submissionPeriod) {
      const startDate = new Date(submissionPeriod.startDate)
      const endDate = new Date(submissionPeriod.endDate)
      return {
        startDate,
        endDate,
      }
    }
    const now = new Date()
    const oneMonthFromNow = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate())
    return {
      startDate: now,
      endDate: oneMonthFromNow,
    }
  })()

  const formDefinition = useF0FormDefinition({
    name: isEditing ? 'edit-submission-period' : 'create-submission-period',
    schema,
    defaultValues,
    onSubmit: async ({ data }) => {
      const startDate = formatDate(data.startDate)
      const endDate = formatDate(data.endDate)

      const result = isEditing
        ? await update(startDate, endDate)
        : await bulkCreate(startDate, endDate)

      if (!result.success) {
        return result
      }

      onClose()
      return { success: true }
    },
    submitConfig: { type: 'default', hideSubmitButton: true },
  })

  return <FactorialF0Form formRef={formRef} formDefinition={formDefinition} />
}

export default ScheduleForLater
